import { Button, buttonRecipe } from "../../../components/core/basic/Button";
import { Badge, Col } from "react-bootstrap";
import { Link } from "@tanstack/react-router";
import { linkOptionsFromHref } from "../../../utils/searchParams";
import { GiPathDistance } from "react-icons/gi";
import { trellisPalette } from "../../../components/custom/analytics/palettes";
import { Wrap } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { HoverInfo } from "../../../components/core/basic/Tooltip";
import { useSimpleCan } from "../../../config/casl/casl";

const ReportTypeCard = ({ query, onSelect, onViewSubscriptions, subscribed, featured, availableSources = [] }) => {
  const { t } = useTranslation(["amc"]);
  const userCanCreateAMCReports = useSimpleCan("create", "AMC");
  const { id, display_name, name, description, byline, required_datasources: requiredSources } = query;

  const hasAccessToReport = useMemo(() => {
    return requiredSources?.every((source) => availableSources.includes(source)) ?? true;
  }, [requiredSources, availableSources]);

  const renderFeaturedReportIcon = (reportName) => {
    const iconBackgroundStyles = {
      width: "40px",
      height: "40px",
      backgroundColor: trellisPalette[10],
      color: "#FFF",
      borderRadius: "100%",
      fontSize: "22px",
    };
    switch (reportName) {
      case "SHOPPER_INSIGHTS":
        return (
          <div
            style={{ ...iconBackgroundStyles, backgroundColor: trellisPalette[3] }}
            className="d-flex align-items-center justify-content-center me-3"
          >
            <i className="pe-7s-cart" style={{ fontWeight: 600 }} />
          </div>
        );
      case "PATH_TO_PURCHASE_GROUPS_INTERNAL":
        return (
          <div style={iconBackgroundStyles} className="d-flex align-items-center justify-content-center me-3">
            <GiPathDistance size={22} style={{ stroke: "#FFF", strokeWidth: "12px" }} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Col xs={12} md={6} className="p-5">
      <div className="border h-100" style={{ padding: "16px 18px", borderRadius: "6px" }}>
        <div className="d-flex flex-column gap-2 justify-content-between h-100">
          <div>
            <h4
              className={`my-0 d-flex gap-1 ${featured ? "" : "px-3"} py-0`}
              style={{ fontWeight: 600, fontSize: "18px" }}
            >
              {featured && <div className="my-auto">{renderFeaturedReportIcon(name)}</div>}{" "}
              <div>
                <span>{typeof display_name === "function" ? t(display_name) : display_name}</span>
                {requiredSources?.length ?
                  <div>
                    <Badge
                      className={`${css({ marginBottom: "10px", borderRadius: "5px", fontSize: "12px" })} badge bg-trellis-purple`}
                      pill
                    >
                      FSI
                    </Badge>
                  </div>
                : null}

                <span
                  style={{ fontWeight: 400, lineHeight: 1.4, fontSize: "13px", marginTop: featured ? "-5px" : "-3px" }}
                  className={`fst-italic  mb-3 d-block`}
                >
                  {typeof byline === "function" ? t(byline) : byline}
                </span>
              </div>
            </h4>

            <p className="fs-5 p-3" style={{ minHeight: "50px" }}>
              {typeof description === "function" ? t(description) : description}
            </p>
          </div>
          <Wrap justifyContent={"space-evenly"} fontSize="medium" width="100%" paddingX="medium" gap="medium">
            {subscribed || featured ?
              ["SHOPPER_INSIGHTS"].includes(name) ?
                <Link
                  {...(featured ?
                    linkOptionsFromHref(query.href)
                  : { to: "/user/amc/dashboard/reports/$id", params: { id: String(id) } })}
                  className={buttonRecipe({ variant: "secondary" })}
                >
                  {t(($) => $.amc["View Report"])}
                </Link>
              : <>
                  <Button variant="secondary" onPress={onSelect} isDisabled={!userCanCreateAMCReports}>
                    {t(($) => $.amc["Create New Subscription"])}
                  </Button>
                  <Button
                    variant="link"
                    onPress={() => {
                      onViewSubscriptions();
                    }}
                  >
                    {t(($) => $.amc["View Subscriptions"])}
                  </Button>
                </>

            : <Button variant="secondary" onPress={onSelect} isDisabled={!userCanCreateAMCReports}>
                {t(($) => $.amc["Subscribe"])}
                {!hasAccessToReport ?
                  <HoverInfo>
                    {t(
                      ($) =>
                        $.amc[
                          "This query requires a subscription to AMCs Flexible Shopper Insights to access the data needed to build this report"
                        ],
                    )}
                  </HoverInfo>
                : null}
              </Button>
            }
          </Wrap>
        </div>
      </div>
    </Col>
  );
};

export default ReportTypeCard;
